import {Injectable} from "@angular/core";
import {DeliveryPoint} from "@app/core/delivery-point/application/dtos/delivery-point";
import {DeliveryPointLocal} from "@app/core/delivery-point/domain/delivery-point.local";
import {DeliveryPointMapper} from "@app/core/delivery-point/infrastructure/mappers/delivery-point.mapper";
import {StorageLocal} from "@app/core/shared/domain/storage/storage.local";
import {BehaviorSubject, Observable} from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class DeliveryPointLocalStorage implements DeliveryPointLocal {
    private CURRENT_DELIVERY_POINT_KEY = 'currentDeliveryPoint';
    private GUEST_CODE = 0;

    private currentDeliveryPointSubject = new BehaviorSubject<DeliveryPoint | null>(null);
    readonly currentDeliveryPoint$: Observable<DeliveryPoint | null> = this.currentDeliveryPointSubject.asObservable();

    private currentDeliveryPointCodeSubject = new BehaviorSubject<number>(this.GUEST_CODE);

    /**
     * Returns the current DeliveryPoint code.
     * If there isn't returns the default DeliveryPoint code (0).
     */
    readonly currentDeliveryPointCode$ = this.currentDeliveryPointCodeSubject.asObservable();

    constructor(
        private localStorage: StorageLocal
    ) {
        const currentDeliveryPoint = this.getSavedDeliveryPoint();
        if (currentDeliveryPoint) {
            this.updateCurrentDeliveryPoint(currentDeliveryPoint)
        }
    }

    updateCurrentDeliveryPoint(deliveryPoint: DeliveryPoint | null) {
        // This order is necessary to avoid that when the value of currentDeliveryPointCode$ is read when currentDeliveryPoint$ changes value, the previous value is read.
        this.currentDeliveryPointCodeSubject.next(deliveryPoint?.code ?? this.GUEST_CODE);
        this.currentDeliveryPointSubject.next(deliveryPoint);
    }

    getDeliveryPointCode(): number {
        return this.currentDeliveryPointCodeSubject.value;
    }

    public getSavedDeliveryPoint(): DeliveryPoint | null {
        const deliveryPointString = this.localStorage.getItem(this.CURRENT_DELIVERY_POINT_KEY)
        if (deliveryPointString) {
            return DeliveryPointMapper.deserialize(JSON.parse(deliveryPointString));
        }
        return null;
    }

    /**
     * Save a delivery point to set it as default delivery point and remove all products of cart.
     */
    public saveDeliveryPoint(deliveryPoint: DeliveryPoint): void {
        this.localStorage.setItem(
            this.CURRENT_DELIVERY_POINT_KEY,
            JSON.stringify(DeliveryPointMapper.serialize(deliveryPoint))
        );
        this.updateCurrentDeliveryPoint(deliveryPoint);
    }

    public deleteCurrentDeliveryPoint(): void {
        this.localStorage.removeItem(this.CURRENT_DELIVERY_POINT_KEY);
        this.updateCurrentDeliveryPoint(null);
    }
}
